import React from 'react';
import { Helmet } from 'react-helmet';
import Icon from '../../components/AppIcon';
import RegistrationForm from './components/RegistrationForm';
import TrustSignals from './components/TrustSignals';
import FeatureHighlights from './components/FeatureHighlights';

const Register = () => {
  return (
    <>
      <Helmet>
        <title>Create Account - AccounTech AI | AI-Powered Accounting Software</title>
        <meta name="description" content="Join thousands of businesses using AccounTech AI for intelligent accounting automation, real-time insights, and streamlined financial management." />
        <meta name="keywords" content="accounting software, AI accounting, business registration, financial management, automated bookkeeping" />
      </Helmet>
      <div className="min-h-screen bg-background">
        {/* Header */}
        <header className="bg-surface border-b border-border">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
                  <Icon name="Calculator" size={20} color="white" />
                </div>
                <span className="text-xl font-semibold text-foreground">AccounTech AI</span>
              </div>
              <div className="text-sm text-muted-foreground">
                Already have an account?{' '}
                <a href="/login" className="text-primary hover:text-primary/80 font-medium transition-colors">
                  Sign In
                </a>
              </div>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            {/* Left Column - Registration Form */}
            <div className="space-y-8">
              <div className="text-center lg:text-left space-y-4">
                <h1 className="text-3xl font-bold text-foreground">
                  Start Your AI-Powered Accounting Journey
                </h1>
                <p className="text-lg text-muted-foreground">
                  Create your account and experience intelligent financial management designed for modern businesses.
                </p>
              </div>

              {/* Registration Form */}
              <div className="bg-surface border border-border rounded-xl p-8 shadow-professional">
                <RegistrationForm />
              </div>

              {/* Trust Signals */}
              <div className="bg-surface border border-border rounded-xl p-8 shadow-professional">
                <TrustSignals />
              </div>
            </div>

            {/* Right Column - Features & Benefits */}
            <div className="space-y-8">
              {/* Feature Highlights */}
              <div className="bg-surface border border-border rounded-xl p-8 shadow-professional">
                <FeatureHighlights />
              </div>

              {/* Testimonial */}
              <div className="bg-primary/5 border border-primary/20 rounded-xl p-8">
                <div className="space-y-4">
                  <div className="flex items-center space-x-1">
                    {[...Array(5)]?.map((_, i) => (
                      <Icon key={i} name="Star" size={16} className="text-warning fill-current" />
                    ))}
                  </div>
                  <blockquote className="text-foreground">
                    "AccounTech AI transformed our accounting process. The AI insights helped us identify cost savings we never knew existed, and the automation saved us 15 hours per week."
                  </blockquote>
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center">
                      <Icon name="User" size={16} color="white" />
                    </div>
                    <div>
                      <p className="font-medium text-foreground">Sarah Johnson</p>
                      <p className="text-sm text-muted-foreground">CEO, TechStart Solutions</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Getting Started Steps */}
              <div className="bg-surface border border-border rounded-xl p-8 shadow-professional">
                <h3 className="text-lg font-semibold text-foreground mb-6">Getting Started is Easy</h3>
                <div className="space-y-4">
                  {[
                    {
                      step: 1,
                      title: 'Create Your Account',
                      description: 'Fill out the registration form with your business details'
                    },
                    {
                      step: 2,
                      title: 'Verify Your Email',
                      description: 'Check your inbox and verify your email address'
                    },
                    {
                      step: 3,
                      title: 'Set Up Your Business',
                      description: 'Configure your chart of accounts and business preferences'
                    },
                    {
                      step: 4,
                      title: 'Start Managing Finances',
                      description: 'Begin creating invoices, tracking expenses, and generating reports'
                    }
                  ]?.map((item) => (
                    <div key={item?.step} className="flex items-start space-x-4">
                      <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-sm font-medium text-white">{item?.step}</span>
                      </div>
                      <div>
                        <h4 className="font-medium text-foreground text-sm">{item?.title}</h4>
                        <p className="text-xs text-muted-foreground mt-1">{item?.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </main>

        {/* Footer */}
        <footer className="bg-surface border-t border-border mt-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="flex flex-col md:flex-row items-center justify-between space-y-4 md:space-y-0">
              <div className="flex items-center space-x-6 text-sm text-muted-foreground">
                <a href="#" className="hover:text-foreground transition-colors">Privacy Policy</a>
                <a href="#" className="hover:text-foreground transition-colors">Terms of Service</a>
                <a href="#" className="hover:text-foreground transition-colors">Support</a>
              </div>
              <div className="text-sm text-muted-foreground">
                © {new Date()?.getFullYear()} AccounTech AI. All rights reserved.
              </div>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
};

export default Register;